import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  MenuItem,
  Snackbar,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";

import {
  Search as SearchIcon,
  TrackChanges as TrackIcon,
} from "@mui/icons-material";
import AssessmentcontrolApiService from "../../../services/AssessmentcontrolApiService";
import { encryptAppId, encryptText } from '../../../utils/encryption';


const formatDate = (dateString) => {
  if (!dateString) return '';
  const datePart = String(dateString).substring(0, 10);
  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}`;
};

const DoAssessmentControl = () => {
  const navigate = useNavigate();

  const [applicationNo, setApplicationNo] = useState("");
  const [applicationType, setApplicationType] = useState("");
  const [appTypeOptions, setAppTypeOptions] = useState([]);
  const [appNoConfirmed, setAppNoConfirmed] = useState(false);
  const [applicationData, setApplicationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem("do_assessmentControl_state");
    if (saved) {
      const s = JSON.parse(saved);
      setApplicationNo(s.applicationNo || "");
      setApplicationType(s.applicationType || "");
      setAppTypeOptions(s.appTypeOptions || []);
      setAppNoConfirmed(s.appNoConfirmed || false);
      setApplicationData(s.applicationData || []);
    }
  }, []);

  const handleAppNoSearch = async () => {
    if (!applicationNo.trim()) {
      setError("Enter Application Number");
      return;
    }
    try {
      setLoading(true);
      setError("");
      setAppNoConfirmed(false);
      setApplicationType("");
      setAppTypeOptions([]);
      setApplicationData([]);

      const result = await AssessmentcontrolApiService.getApplicationTypeByAppNo(applicationNo.trim());
      if (result.success && result.data.length > 0) {
        setAppTypeOptions(result.data);
        setAppNoConfirmed(true);
      } else {
        setError("Application not found");
      }
    } catch (err) {
      setError("Application not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!applicationNo || !applicationType) {
      setError("Enter Application Number and select Application Type");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result = await AssessmentcontrolApiService.getCABDetails({
        applicationno: applicationNo,
        applicationType
      });

      if (result.data && result.data.length > 0) {
        setApplicationData(result.data);
        sessionStorage.setItem("do_assessmentControl_state", JSON.stringify({
          applicationNo,
          applicationType,
          appTypeOptions,
          appNoConfirmed,
          applicationData: result.data,
        }));
      } else {
        setApplicationData([]);
        setError("Application not found");
      }
    } catch (err) {
      setError("Application not found");
      setApplicationData([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "LA":       return "secondary";
      case "TL":       return "primary";
      case "LATA":     return "warning";
      case "TA":       return "success";
      case "Observer": return "info";
      case "TE":       return "error";
      default:         return "default";
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh", py: 2 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Step 1: Search */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Step 1: Search Application
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              label="Application No"
              value={applicationNo}
              onChange={(e) => {
                setApplicationNo(e.target.value);
                setAppNoConfirmed(false);
                setApplicationType("");
                setAppTypeOptions([]);
                setApplicationData([]);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && applicationNo.trim())
                  handleAppNoSearch();
              }}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              onClick={handleAppNoSearch}
              disabled={loading}
              sx={{
                bgcolor: "#00024c",
                "&:hover": { bgcolor: "#000380" },
                height: 40,
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        {appNoConfirmed && appTypeOptions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Application Type</InputLabel>
              <Select
                value={applicationType}
                label="Select Application Type"
                onChange={(e) => {
                  setApplicationType(e.target.value);
                  setApplicationData([]);
                  setError("");
                }}
              >
                <MenuItem value="">-- Select --</MenuItem>
                {appTypeOptions.map((opt) => (
                  <MenuItem key={opt.AppId} value={opt.ApplicationTypeName}>
                    {opt.ApplicationTypeName}
                    {opt.LabTypeName ? ` (${opt.LabTypeName})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {applicationType && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                bgcolor: "#E34124",
                "&:hover": { bgcolor: "#c73518" },
                height: 40,
                px: 3,
              }}
            >
              Load Details
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Application Summary */}
      {applicationData.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Application No
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {applicationData[0].ApplicationNo}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Stage
                </Typography>
                <Chip
                  label={applicationData[0].Stage}
                  color="info"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Assessors
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {applicationData.length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Assessor Table */}
      {applicationData.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Assessor Allotment Details
              </Typography>

              {applicationData?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {/* View Report */}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      window.open(
                        `/main/do/form217-preview?appId=${encryptAppId(
                          applicationData[0].AppId,
                        )}&assessmentId=${encryptText(
                          applicationData[0].AssessorAllotmentId,
                        )}&assessorId=${encryptText(
                          applicationData[0].AllotedToId,
                        )}`,
                        "_blank",
                      )
                    }
                    sx={{
                      backgroundColor: "#2e7d32",
                      "&:hover": {
                        backgroundColor: "#1b5e20",
                      },
                      textTransform: "none",
                    }}
                  >
                    View Report
                  </Button>

                  {/* View NC Report */}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      window.open(
                        `/main/do/nc-report?appId=${encryptAppId(
                          applicationData[0].AppId,
                        )}&assessmentId=${encryptText(
                          applicationData[0].AssessorAllotmentId,
                        )}&assessorId=${encryptText(
                          applicationData[0].AllotedToId,
                        )}`,
                        "_blank",
                      )
                    }
                    sx={{
                      backgroundColor: "#c62828",
                      "&:hover": {
                        backgroundColor: "#b71c1c",
                      },
                      textTransform: "none",
                    }}
                  >
                    View NC Report
                  </Button>
                  {/* Application Finding */}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      const url =
                        applicationData[0].LabTypeId === 1
                          ? `/main/assessment-report-testing?appId=${encryptAppId(
                              applicationData[0].AppId,
                            )}&assessmentId=${encryptText(
                              applicationData[0].AssessorAllotmentId,
                            )}&orgId=${encryptText(
                              applicationData[0].OrgId,
                            )}&observation=${encryptText("true")}`
                          : applicationData[0].LabTypeId === 2
                            ? `/main/assessment-report-calibration?appId=${encryptAppId(
                                applicationData[0].AppId,
                              )}&assessmentId=${encryptText(
                                applicationData[0].AssessorAllotmentId,
                              )}&orgId=${encryptText(
                                applicationData[0].OrgId,
                              )}&observation=${encryptText("true")}`
                            : `/main/assessment-report?appId=${encryptAppId(
                                applicationData[0].AppId,
                              )}&assessmentId=${encryptText(
                                applicationData[0].AssessorAllotmentId,
                              )}&orgId=${encryptText(
                                applicationData[0].OrgId,
                              )}&observation=${encryptText("true")}`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    sx={{
                      backgroundColor: "#c62828",
                      "&:hover": {
                        backgroundColor: "#b71c1c",
                      },
                      textTransform: "none",
                    }}
                  >
                    Application Finding
                  </Button>
                </Box>
              )}
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr.No</TableCell>
                    <TableCell>Assessor Name</TableCell>
                    <TableCell>Login</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Date From</TableCell>
                    <TableCell>Date To</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {applicationData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{index + 1}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={500}>
                          {row.assessorName}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.assessorLogin}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.Category}
                          color={getCategoryColor(row.Category)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 250 }}>
                        {row.disciplineName}
                      </TableCell>

                      <TableCell>{formatDate(row.DateFrom)}</TableCell>

                      <TableCell>{formatDate(row.DateTo)}</TableCell>

                      <TableCell>
                        <Chip
                          label={row.Location === 1 ? "Yes" : "No"}
                          color={row.Location === 1 ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<TrackIcon />}
                          onClick={() =>
                            navigate(
                              `/main/do/assessment-tracking-log?appId=${encodeURIComponent(row.AppId || "")}&allotedToId=${encodeURIComponent(row.AllotedToId || "")}`,
                            )
                          }
                          sx={{
                            backgroundColor: "#0b3b8c",
                            "&:hover": { backgroundColor: "#082d6e" },
                            textTransform: "none",
                            fontSize: "0.7rem",
                          }}
                        >
                          Tracking Log
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DoAssessmentControl;